import type { ReactNode } from "react";

// Presentational stepper for guided money-movement workflows.
//
// Pure display — it holds no state and triggers no actions. Each page
// computes its own step states from the row/proposal/request status and
// passes them in. The stepper exists to make the canonical sequence
// (Review → Provide/Create proposal → Approve → Execute) legible so an
// operator never confuses which stage they are at, and never executes
// funds before a proposal is approved.

export type StepState = "done" | "current" | "upcoming" | "blocked";

export interface MoneyFlowStep {
  /** Short label, e.g. "Review request". */
  label: string;
  /** One-line hint shown under the label. */
  hint?: ReactNode;
  state: StepState;
}

const DOT_BG: Record<StepState, string> = {
  done: "var(--ok, #0a9444)",
  current: "var(--accent, #2c7be5)",
  upcoming: "var(--border, #ccc)",
  blocked: "var(--border, #ccc)",
};

const LABEL_FG: Record<StepState, string> = {
  done: "var(--fg)",
  current: "var(--fg)",
  upcoming: "var(--fg-dim)",
  blocked: "var(--fg-dim)",
};

function dotGlyph(state: StepState, index: number): string {
  if (state === "done") return "✓";
  if (state === "blocked") return "–";
  return String(index + 1);
}

export function MoneyFlowStepper({
  steps,
  ariaLabel = "Workflow steps",
}: {
  steps: ReadonlyArray<MoneyFlowStep>;
  ariaLabel?: string;
}) {
  return (
    <ol
      aria-label={ariaLabel}
      style={{
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        margin: "12px 0",
        padding: 0,
      }}
    >
      {steps.map((s, i) => (
        <li
          key={s.label}
          aria-current={s.state === "current" ? "step" : undefined}
          data-step-state={s.state}
          style={{
            flex: "1 1 160px",
            minWidth: 160,
            border:
              s.state === "current"
                ? "1px solid var(--accent, #2c7be5)"
                : "1px solid var(--border, #ccc)",
            borderRadius: 6,
            padding: "8px 10px",
            background:
              s.state === "current" ? "rgba(44, 123, 229, 0.06)" : "var(--bg-card)",
            opacity: s.state === "upcoming" || s.state === "blocked" ? 0.7 : 1,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              aria-hidden
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: DOT_BG[s.state],
                color: s.state === "upcoming" || s.state === "blocked" ? "var(--fg-dim)" : "white",
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {dotGlyph(s.state, i)}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: LABEL_FG[s.state] }}>
              {s.label}
            </span>
          </div>
          {s.hint && (
            <div style={{ marginTop: 4, fontSize: 11, color: "var(--fg-dim)", lineHeight: 1.35 }}>
              {s.hint}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
